import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Star, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadCaptureForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    zipCode: '',
    garageSize: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.zipCode || !formData.garageSize) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, formData);
      
      // Track Meta Pixel conversion event
      if (window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: 'Lead Form Submission',
          content_category: 'Garage Cleaning Lead',
          value: 0.00,
          currency: 'USD'
        });
      }
      
      // Redirect to thank you page
      navigate('/thank-you');
    } catch (error) {
      console.error('Error submitting lead:', error);
      toast.error('Failed to submit request. Please try again or call us directly.');
      setLoading(false);
    }
  };

  return (
    <Card className="bg-white shadow-2xl">
      <CardHeader className="text-center pb-4 bg-emerald-600 text-white rounded-t-lg">
        <div className="flex justify-center mb-2">
          <div className="flex gap-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
            ))}
          </div>
        </div>
        <CardTitle className="text-2xl md:text-3xl font-bold">
          Request Your FREE Quote!
        </CardTitle>
        <p className="text-emerald-100 text-sm mt-2">
          Get a personalized quote within 24 hours
        </p>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName" className="text-slate-700 font-medium">
                First Name *
              </Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                placeholder="John"
                className="mt-1"
                required
              />
            </div>
            <div>
              <Label htmlFor="lastName" className="text-slate-700 font-medium">
                Last Name *
              </Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                placeholder="Smith"
                className="mt-1"
                required
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="email" className="text-slate-700 font-medium">
              Email Address *
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="john@example.com"
              className="mt-1"
              required
            />
          </div>
          
          <div>
            <Label htmlFor="phone" className="text-slate-700 font-medium">
              Phone Number *
            </Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="(555) 123-4567"
              className="mt-1"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="zipCode" className="text-slate-700 font-medium">
                Zip Code *
              </Label>
              <Input
                id="zipCode"
                type="text"
                maxLength={5}
                value={formData.zipCode}
                onChange={(e) => setFormData({ ...formData, zipCode: e.target.value.replace(/\D/g, '') })}
                placeholder="32792"
                className="mt-1"
                required
              />
            </div>
            <div>
              <Label htmlFor="garageSize" className="text-slate-700 font-medium">
                Garage Size *
              </Label>
              <Select value={formData.garageSize} onValueChange={(value) => setFormData({ ...formData, garageSize: value })}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1-car">1 Car</SelectItem>
                  <SelectItem value="2-car">2 Car</SelectItem>
                  <SelectItem value="3-car">3 Car</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <Label htmlFor="message" className="text-slate-700 font-medium">
              Additional Details (Optional)
            </Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Tell us about any specific needs or questions..."
              className="mt-1"
              rows={3}
            />
          </div>
          
          <Button 
            type="submit" 
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg font-semibold"
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Request FREE Quote'}
            <Send className="ml-2 h-5 w-5" />
          </Button>
          
          <p className="text-xs text-center text-slate-500">
            By submitting, you agree to be contacted about your request. No spam, just a friendly quote!
          </p>
        </form>
      </CardContent>
    </Card>
  );
};

export default LeadCaptureForm;